'use client';

import type { SupabaseClient } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { Database } from '~/lib/database.types';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import type {
  StatKey,
  StatsStripSectionConfig,
} from '~/(tenant)/_lib/schema/native-home-config.schema';
import {
  type FamilyBookingEntry,
  classifyBooking,
} from '~/(tenant)/activity/_lib/member-activity';
import { getFamilyBookingsAction } from '~/(tenant)/bookings/_lib/server/family-bookings-action';

interface StatsStripSectionProps {
  config: StatsStripSectionConfig;
  accountId: string;
}

const STAT_LABEL_KEYS: Record<StatKey, string> = {
  classes_attended: 'common:nativeHome.stats.classesAttended',
  credits_remaining: 'common:nativeHome.stats.creditsRemaining',
  streak_days: 'common:nativeHome.stats.streakDays',
  bookings_made: 'common:nativeHome.stats.bookingsMade',
  hours_played: 'common:nativeHome.stats.hoursPlayed',
  membership_tier: 'common:nativeHome.stats.membershipTier',
};

/**
 * 2-4 stats in a hairline-bordered row. Display serif numbers, mono labels.
 * No card chrome — just borderTop/Bottom + vertical dividers between cells.
 *
 * Hides entirely when not authenticated.
 */
export function StatsStripSection({
  config,
  accountId,
}: StatsStripSectionProps) {
  const t = useNativeTokens();
  const { t: translate } = useTranslation();
  const client = useSupabase();

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
  });

  const userId = session?.user?.id;
  // streak_days was a placeholder that counted booking days, not a streak.
  // It is no longer offered; saved configs that still list it skip it.
  const wanted = config.stats.filter((key) => key !== 'streak_days');

  const { data: stats } = useQuery({
    queryKey: ['stats-strip', userId, accountId, wanted.join(',')],
    queryFn: async () => {
      if (!userId) return null;
      return await fetchStats(client, userId, accountId, wanted);
    },
    enabled: !!userId,
    staleTime: 60_000,
  });

  if (!userId || !stats || wanted.length === 0) return null;

  return (
    <section
      style={{
        margin: '24px 22px',
        padding: '20px 4px',
        borderTop: `1px solid ${t.line}`,
        borderBottom: `1px solid ${t.line}`,
        display: 'grid',
        gridTemplateColumns: `repeat(${wanted.length}, 1fr)`,
      }}
    >
      {wanted.map((key, i) => {
        const value = stats[key];
        return (
          <div
            key={key}
            style={{
              textAlign: 'center',
              borderLeft: i > 0 ? `1px solid ${t.line}` : 'none',
            }}
          >
            <div
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 28,
                fontWeight: 300,
                color: t.foreground,
                lineHeight: 1,
              }}
            >
              {value ?? '—'}
            </div>
            <div
              style={{
                fontFamily: t.fontMono,
                fontSize: 8,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: t.muted,
                marginTop: 6,
              }}
            >
              {translate(STAT_LABEL_KEYS[key])}
            </div>
          </div>
        );
      })}
    </section>
  );
}

const toMinutes = (time: string | null | undefined) => {
  const [hours = 0, minutes = 0] = (time ?? '00:00').split(':').map(Number);
  return hours * 60 + minutes;
};

// Sessions can cross midnight (e.g. 23:00–01:00): end <= start means the
// session ends the next day.
function sessionMinutes(b: FamilyBookingEntry): number {
  const duration = toMinutes(b.end_time) - toMinutes(b.start_time);
  return duration > 0 ? duration : duration + 24 * 60;
}

function sessionEnd(b: FamilyBookingEntry): Date {
  const start = new Date(`${b.booking_date}T${b.start_time ?? '00:00'}`);
  return new Date(start.getTime() + sessionMinutes(b) * 60_000);
}

function isCancelled(b: FamilyBookingEntry): boolean {
  return (
    b.booking_status === 'cancelled' || b.participant_status === 'cancelled'
  );
}

// Stats are best-effort — a failed query leaves its stat null.
async function fetchStats(
  client: SupabaseClient<Database>,
  userId: string,
  accountId: string,
  keys: readonly StatKey[],
): Promise<Record<StatKey, string | number | null>> {
  const wanted = new Set(keys);
  const result: Record<StatKey, string | number | null> = {
    classes_attended: null,
    credits_remaining: null,
    streak_days: null,
    bookings_made: null,
    hours_played: null,
    membership_tier: null,
  };

  const promises: Promise<void>[] = [];

  if (wanted.has('credits_remaining')) {
    promises.push(
      (async () => {
        const nowIso = new Date().toISOString();
        const { data } = await client
          .rpc('get_customer_booking_packages', { p_user_id: userId })
          .select('remaining_booking_count')
          .eq('user_id', userId)
          .eq('account_id', accountId)
          .eq('status', 'active')
          .gt('expires_at', nowIso);
        const rows = (data ?? []) as Array<{
          remaining_booking_count?: number | null;
        }>;
        const total = rows.reduce(
          (sum, r) => sum + (r.remaining_booking_count ?? 0),
          0,
        );
        result.credits_remaining = total;
      })(),
    );
  }

  const needsBookings =
    wanted.has('classes_attended') ||
    wanted.has('bookings_made') ||
    wanted.has('hours_played');

  if (needsBookings) {
    promises.push(
      (async () => {
        // Through the server action, not the browser client: class enrollments
        // join session rows the customer can't read under RLS, so a direct
        // RPC call silently drops every class (classes_attended was always 0).
        const response = await getFamilyBookingsAction({ includePast: true });
        if (!response.success) return;

        const all = response.data.flatMap((account) =>
          Array.isArray(account.bookings)
            ? (account.bookings as FamilyBookingEntry[])
            : [],
        );
        const now = new Date();
        const kept = all.filter((b) => !isCancelled(b));
        const finished = kept.filter(
          (b) => b.participant_status !== 'no_show' && sessionEnd(b) <= now,
        );

        if (wanted.has('classes_attended')) {
          // Same rule as the /activity page: a class counts once it has ended
          // and wasn't cancelled or marked no-show.
          result.classes_attended = finished.filter(
            (b) => classifyBooking(b) === 'class',
          ).length;
        }

        if (wanted.has('bookings_made')) {
          result.bookings_made = kept.length;
        }

        if (wanted.has('hours_played')) {
          const minutes = finished.reduce(
            (sum, b) => sum + sessionMinutes(b),
            0,
          );
          result.hours_played = Math.round(minutes / 60);
        }
      })(),
    );
  }

  if (wanted.has('membership_tier')) {
    promises.push(
      (async () => {
        const { data } = await client
          .from('user_memberships')
          .select('membership_plan:plan_id(name)')
          .eq('user_id', userId)
          .eq('account_id', accountId)
          .eq('status', 'active')
          .eq('is_deleted', false)
          .maybeSingle();
        const tier = (data as { membership_plan?: { name?: string } } | null)
          ?.membership_plan?.name;
        result.membership_tier = tier ?? null;
      })(),
    );
  }

  await Promise.all(promises);
  return result;
}
